import { DoubleSide, Group, Matrix4, Mesh } from "three";
import { IFCELEMENTQUANTITY, IFCMATERIAL, IFCMATERIALLAYER, IFCMATERIALLAYERSET, IFCMATERIALLAYERSETUSAGE, IFCMATERIALLIST, IFCPROPERTYSET, IFCPROPERTYSINGLEVALUE, IFCQUANTITYAREA, IFCQUANTITYCOUNT, IFCQUANTITYLENGTH, IFCQUANTITYTIME, IFCQUANTITYVOLUME, IFCQUANTITYWEIGHT, } from "web-ifc";
import { constructMeshThreeMaterial } from "../../../impl/objects";
import { ObjectType } from "../../picking";
export class Ifc3D extends Group {
    constructor(options) {
        super();
        this.ifcApi = options.ifcApi;
        this.modelId = options.modelId;
        this.objectId = options.objectId;
        this.geometry = options.mesh.geometry;
        this.coordinationMatrix = options.mesh.matrix;
        this.elementGroups = options.rangeGroups;
        this.defaultGroups = this.geometry.groups.map((group) => {
            const { start, count, materialIndex } = group;
            const threeMaterial = options.mesh.material[materialIndex];
            return { start, count, threeMaterial };
        });
        this.pickingManager = options.pickingManager;
        this.groups = this.defaultGroups.map((group) => (Object.assign({}, group)));
        this.materialSet = new Map();
        this.elementMaterials = new Map();
        this.recycledMeshes = [];
        this.linesById = new Map();
        this.elementIdsByType = new Map();
    }
    findContainingGroup(index) {
        // Binary search for the group containing the index
        let l = 0;
        let r = this.groups.length - 1;
        while (l <= r) {
            const m = Math.floor((l + r) / 2);
            // Test if in range
            const group = this.groups[m];
            const headIndex = group.start;
            const tailIndex = group.start + group.count - 1;
            if (index < headIndex) {
                r = m - 1;
            }
            else if (index > tailIndex) {
                l = m + 1;
            }
            else {
                return m;
            }
        }
        return l;
    }
    insertGeometryGroup(elementId, threeMaterial) {
        const indexRanges = this.elementGroups.get(elementId);
        if (indexRanges === undefined)
            return;
        // Update the geometry groups
        for (const range of indexRanges) {
            // Find groups containing range head and tail
            const headIndex = this.findContainingGroup(range.start);
            const tailIndex = this.findContainingGroup(range.start + range.count - 1);
            // Define new group
            const head = this.groups[headIndex];
            const tail = this.groups[tailIndex];
            const newGroup = {
                start: range.start,
                count: range.count,
                threeMaterial,
            };
            if (headIndex === tailIndex) {
                // Handle case where range is contained in a single group (and materials are different)
                if (head.threeMaterial !== threeMaterial) {
                    const left = {
                        start: head.start,
                        count: range.start - head.start,
                        threeMaterial: head.threeMaterial,
                    };
                    const right = {
                        start: range.start + range.count,
                        count: head.start + head.count - (range.start + range.count),
                        threeMaterial: head.threeMaterial,
                    };
                    // Splice
                    if (left.count > 0 && right.count > 0) {
                        this.groups.splice(headIndex, 1, left, newGroup, right);
                    }
                    else if (left.count > 0) {
                        this.groups.splice(headIndex, 1, left, newGroup);
                    }
                    else if (right.count > 0) {
                        this.groups.splice(headIndex, 1, newGroup, right);
                    }
                    else {
                        this.groups.splice(headIndex, 1, newGroup);
                    }
                }
            }
            else {
                // Handle case where range overlaps with multiple groups
                head.count = range.start - head.start;
                // Update tail offset and count
                const newTailStart = range.start + range.count;
                tail.count = tail.start + tail.count - newTailStart;
                tail.start = newTailStart;
                // Splice
                if (head.count > 0 && tail.count > 0) {
                    this.groups.splice(headIndex + 1, tailIndex - headIndex - 1, newGroup);
                }
                else if (tail.count > 0) {
                    this.groups.splice(headIndex, tailIndex - headIndex, newGroup);
                }
                else if (head.count > 0) {
                    this.groups.splice(headIndex + 1, tailIndex - headIndex, newGroup);
                }
                else {
                    this.groups.splice(headIndex, tailIndex - headIndex + 1, newGroup);
                }
            }
        }
        // Combine consecutive groups with the same material
        let runCount = 0;
        for (let i = this.groups.length - 1; i >= 1; i--) {
            const curr = this.groups[i];
            const next = this.groups[i - 1];
            if (curr.threeMaterial === next.threeMaterial) {
                runCount++;
                next.count += curr.count;
            }
            else {
                this.groups.splice(i + 1, runCount);
                runCount = 0;
            }
        }
        this.groups.splice(1, runCount);
    }
    getThreeMaterial(material) {
        const key = JSON.stringify(material);
        let threeMaterial = this.materialSet.get(key);
        if (threeMaterial === undefined) {
            // Register the new material
            threeMaterial = constructMeshThreeMaterial(material, [], this.objectId, true, false);
            threeMaterial.transparent = threeMaterial.opacity < 1;
            threeMaterial.depthWrite = !threeMaterial.transparent;
            threeMaterial.forceSinglePass = true;
            threeMaterial.side = DoubleSide;
            this.materialSet.set(key, threeMaterial);
        }
        return threeMaterial;
    }
    clear() {
        this.pickingManager.removeForId(this.objectId);
        for (const child of this.children) {
            this.pickingManager.removeForObject(child.id);
            this.recycledMeshes.push(child);
        }
        return super.clear();
    }
    buildMeshes() {
        if (this.owner === undefined)
            throw new Error("Someone forgot to set the owner for a Tileset3D");
        this.clear();
        for (const group of this.groups) {
            if (!group.threeMaterial)
                continue;
            // Recycle previously created meshes (and their BufferGeometry objects)
            let mesh = this.recycledMeshes.pop();
            if (mesh === undefined) {
                mesh = new Mesh(undefined, group.threeMaterial);
                // Avoid recomputing geometry
                mesh.geometry.attributes = this.geometry.attributes;
                mesh.geometry.index = this.geometry.index;
                mesh.geometry.boundsTree = this.geometry.boundsTree;
                mesh.geometry.boundingSphere = this.geometry.boundingSphere;
                this.coordinationMatrix.decompose(mesh.position, mesh.quaternion, mesh.scale);
                mesh.updateMatrix();
            }
            // Set index buffer and material
            mesh.geometry.setDrawRange(group.start, group.count);
            mesh.material = group.threeMaterial;
            // Register the mesh for picking
            this.pickingManager.setForId(this.objectId, {
                objectType: ObjectType.Composite,
                rootObject: this.owner,
                subObject: mesh,
            });
            this.pickingManager.setIdForObject(mesh.id, this.objectId);
            this.add(mesh);
        }
    }
    refreshGroups() {
        this.groups = this.defaultGroups.map((group) => (Object.assign({}, group)));
        for (const [elementId, material] of this.elementMaterials) {
            if (material) {
                const threeMaterial = this.getThreeMaterial(material);
                this.insertGeometryGroup(elementId, threeMaterial);
            }
            else {
                this.insertGeometryGroup(elementId, null);
            }
        }
        this.buildMeshes();
    }
    setGlobalMaterial(material) {
        const threeMaterial = this.getThreeMaterial(material);
        this.groups = [
            {
                start: 0,
                count: this.geometry.index.count,
                threeMaterial,
            },
        ];
        this.buildMeshes();
    }
    getBoundingSphere() {
        this.updateWorldMatrix(true, false);
        const transform = new Matrix4().multiplyMatrices(this.matrixWorld, this.coordinationMatrix);
        return this.geometry.boundingSphere.clone().applyMatrix4(transform);
    }
    getPhysicalElementIds() {
        return [...this.elementGroups.keys()];
    }
    getLine(id) {
        const cached = this.linesById.get(id);
        if (cached)
            return cached;
        try {
            const line = this.ifcApi.GetLine(this.modelId, id);
            this.linesById.set(id, line);
            return line;
        }
        catch (_a) {
            console.error(`IFC error: failed to get line ${id}`);
            return null;
        }
    }
    getElementIdsOfType(type) {
        const cached = this.elementIdsByType.get(type);
        if (cached)
            return cached;
        const ids = [];
        const code = this.ifcApi.GetTypeCodeFromName(type);
        const array = this.ifcApi.GetLineIDsWithType(this.modelId, code);
        for (let i = 0; i < array.size(); i++) {
            ids.push(array.get(i));
        }
        this.elementIdsByType.set(type, ids);
        return ids;
    }
    getRootElementId() {
        return this.getElementIdsOfType("IFCPROJECT")[0];
    }
    getChildren(elementId) {
        const spatialRelIds = this.getElementIdsOfType("IFCRELCONTAINEDINSPATIALSTRUCTURE");
        const aggregateRelIds = this.getElementIdsOfType("IFCRELAGGREGATES");
        // Relating == parent, Related == child
        // Find the IDs of all related elements
        const relatedIds = [];
        for (const relId of spatialRelIds) {
            const rel = this.getLine(relId);
            if (rel === null)
                continue;
            if (rel.RelatingStructure.value === elementId) {
                if (Array.isArray(rel.RelatedElements)) {
                    const ids = rel.RelatedElements.map((object) => object.value);
                    relatedIds.push(...ids);
                }
                else {
                    relatedIds.push(rel.RelatedElements.value);
                }
            }
        }
        for (const relId of aggregateRelIds) {
            const rel = this.getLine(relId);
            if (rel === null)
                continue;
            if (rel.RelatingObject.value === elementId) {
                if (Array.isArray(rel.RelatedObjects)) {
                    const ids = rel.RelatedObjects.map((object) => object.value);
                    relatedIds.push(...ids);
                }
                else {
                    relatedIds.push(rel.RelatedObjects.value);
                }
            }
        }
        return relatedIds;
    }
    processElementProperty(propertyId) {
        var _a, _b;
        const pLine = this.getLine(propertyId);
        if (pLine === null)
            return null;
        const key = pLine.Name.value;
        let value;
        switch (pLine.type) {
            case IFCPROPERTYSINGLEVALUE:
                value = (_b = (_a = pLine.NominalValue) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : null;
                break;
            default:
                return null;
        }
        return { key, value };
    }
    processElementQuantity(quantityId) {
        const qLine = this.getLine(quantityId);
        if (qLine === null)
            return null;
        const key = qLine.Name.value;
        let value;
        switch (qLine.type) {
            case IFCQUANTITYCOUNT:
                value = qLine.CountValue.value;
                break;
            case IFCQUANTITYLENGTH:
                value = qLine.LengthValue.value;
                break;
            case IFCQUANTITYAREA:
                value = qLine.AreaValue.value;
                break;
            case IFCQUANTITYVOLUME:
                value = qLine.VolumeValue.value;
                break;
            case IFCQUANTITYWEIGHT:
                value = qLine.WeightValue.value;
                break;
            case IFCQUANTITYTIME:
                value = qLine.TimeValue.value;
                break;
            default:
                return null;
        }
        return { key, value };
    }
    getPropertySets(elementId) {
        const relIds = this.getElementIdsOfType("IFCRELDEFINESBYPROPERTIES");
        const psetIds = [];
        for (const relId of relIds) {
            const rel = this.getLine(relId);
            if (rel === null)
                continue;
            let found = false;
            if (Array.isArray(rel.RelatedObjects)) {
                const ids = rel.RelatedObjects.map((object) => object.value);
                found = ids.includes(elementId);
            }
            else {
                found = rel.RelatedObjects.value === elementId;
            }
            if (found) {
                psetIds.push(rel.RelatingPropertyDefinition.value);
            }
        }
        const properties = [];
        for (const psetId of psetIds) {
            const psetLine = this.getLine(psetId);
            if (psetLine === null)
                continue;
            const name = psetLine.Name.value;
            const fields = [];
            if (psetLine.type === IFCPROPERTYSET) {
                psetLine.HasProperties.forEach((p) => {
                    const property = this.processElementProperty(p.value);
                    if (property !== null) {
                        fields.push(property);
                    }
                });
            }
            else if (psetLine.type === IFCELEMENTQUANTITY) {
                // psetLine.Quantities.forEach((p: { value: number, type: number }) => {
                //     const quantity = this.processElementQuantity(p.value);
                //     if (quantity !== null) {
                //         fields.push(quantity);
                //     }
                // });
            }
            // Register the property set
            if (fields.length > 0) {
                properties.push({ name, fields });
            }
        }
        return properties;
    }
    getMaterialSets(elementId) {
        const relIds = this.getElementIdsOfType("IFCRELASSOCIATESMATERIAL");
        const msetIds = [];
        for (const relId of relIds) {
            const rel = this.getLine(relId);
            if (rel === null)
                continue;
            let found = false;
            if (Array.isArray(rel.RelatedObjects)) {
                const ids = rel.RelatedObjects.map((object) => object.value);
                found = ids.includes(elementId);
            }
            else {
                found = rel.RelatedObjects.value === elementId;
            }
            if (found) {
                msetIds.push(rel.RelatingMaterial.value);
            }
        }
        const materials = [];
        for (const msetId of msetIds) {
            const mset = this.getLine(msetId);
            if (mset === null)
                continue;
            if (mset.type === IFCMATERIAL) {
                materials.push(mset.Name.value);
            }
            else if (mset.type === IFCMATERIALLIST) {
                mset.Materials.forEach((m) => {
                    const material = this.getLine(m.value);
                    if (material) {
                        materials.push(material.Name.value);
                    }
                });
            }
            else if (mset.type === IFCMATERIALLAYERSETUSAGE) {
                // Process this
            }
            else if (mset.type === IFCMATERIALLAYERSET) {
                // Process this
            }
            else if (mset.type === IFCMATERIALLAYER) {
                // Process this
            }
        }
        return materials;
    }
    getElementProperties(elementId) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        try {
            const line = this.getLine(elementId);
            if (line === null)
                return null;
            const id = line.expressID;
            const guid = (_b = (_a = line.GlobalId) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : "";
            const type = this.ifcApi.GetNameFromTypeCode(line.type);
            const name = (_d = (_c = line.Name) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : "";
            const description = (_f = (_e = line.Description) === null || _e === void 0 ? void 0 : _e.value) !== null && _f !== void 0 ? _f : "";
            const objectType = (_h = (_g = line.ObjectType) === null || _g === void 0 ? void 0 : _g.value) !== null && _h !== void 0 ? _h : null;
            const tag = (_k = (_j = line.Tag) === null || _j === void 0 ? void 0 : _j.value) !== null && _k !== void 0 ? _k : null;
            const properties = this.getPropertySets(elementId);
            const materials = this.getMaterialSets(elementId);
            const isPhysicalMesh = this.elementGroups.has(elementId);
            const children = this.getChildren(elementId);
            return {
                id,
                guid,
                type,
                name,
                description,
                objectType,
                tag,
                properties,
                materials,
                isPhysicalMesh,
                children,
            };
        }
        catch (e) {
            console.error(e);
            return null;
        }
    }
}
